import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  adminBulkDeleteTransaction,
  BULK_UPDATE_USER_TRANSACTIONS_KEY,
  BulkUpdatePayload,
  bulkUpdateUserTransactions,
  POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY,
} from '@/actions/adminActions'
import { BulkUpdateTransactionDialog } from '../BulkUpdateTransactionDialog'
import { BulkConfirmTransactionDialog } from '../BulkConfirmTransactionDialog'
import {
  clearSelectedTransactions,
  getSelectedTransactionIds,
} from '@/reducers/admin/selectedTransactionsReducer'
import RuleCreationModal from '@/features/admin/transaction-rules/index'
import { useReselector } from '@/utils/sharedHooks'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'
import { Alert, Button, Dialog, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  getFetchError,
  invalidateFetch,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'

export type OnActionParams =
  | {
      type: 'delete'
      transactionIds: number[]
    }
  | {
      type:
        | 'unconfirm'
        | 'confirm'
        | 'exclude'
        | 'needsClarification'
        | 'update'
      transactions: Transaction[]
    }

type BulkActionType =
  | 'unconfirm'
  | 'confirm'
  | 'exclude'
  | 'needsClarification'
  | 'delete'

const fetchKeyFor = (action: BulkActionType) =>
  action === 'delete'
    ? POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY
    : BULK_UPDATE_USER_TRANSACTIONS_KEY

const BulkTransactionActions = ({
  selectedTransactions,
  onAction = (_: OnActionParams) => undefined,
  userId,
}: {
  selectedTransactions: Transaction[]
  onAction?: (params: OnActionParams) => void
  userId?: number
}) => {
  const dispatch = useAppDispatch()
  const [excludeOpen, setExcludeOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [unconfirmOpen, setUnconfirmOpen] = useState(false)
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false)
  const [ruleModalOpen, setRuleModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const selectedTransactionKeys = useReselector(getSelectedTransactionIds)
  const someUnconfirmed = useMemo(
    () => selectedTransactions?.some((tx) => !tx.confirmedAt) ?? true,
    [selectedTransactions]
  )
  const someConfirmed = useMemo(
    () => selectedTransactions?.some((tx) => tx.confirmedAt) ?? false,
    [selectedTransactions]
  )

  const isSaving = useReselector(selectIsFetchingForKeys, [
    POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY,
    BULK_UPDATE_USER_TRANSACTIONS_KEY,
  ])

  const deleteError = useReselector(
    getFetchError,
    POST_ADMIN_BULK_DELETE_TRANSACTIONS_KEY
  )
  const updateError = useReselector(
    getFetchError,
    BULK_UPDATE_USER_TRANSACTIONS_KEY
  )
  const [failedAction, setFailedAction] = useState<BulkActionType | null>(null)
  const failedActionRef = useRef<BulkActionType | null>(null)
  const requestRef = useRef(0)

  const markFailed = (action: BulkActionType, request: number) => {
    if (request !== requestRef.current) {
      dispatch(invalidateFetch(fetchKeyFor(action)))
      return
    }
    failedActionRef.current = action
    setFailedAction(action)
  }

  // Four actions share their fetch key with BulkUpdateTransactionDialog and
  // ReconciliationPanel, so a refusal left in the store would show up there.
  const clearFailure = useCallback(() => {
    if (failedActionRef.current) {
      dispatch(invalidateFetch(fetchKeyFor(failedActionRef.current)))
    }
    failedActionRef.current = null
    setFailedAction(null)
  }, [dispatch])

  // A refusal is about the rows that were selected when it came back.
  useEffect(() => {
    requestRef.current += 1
    clearFailure()
  }, [selectedTransactionKeys, clearFailure])

  useEffect(
    () => () => {
      requestRef.current += 1
      if (failedActionRef.current) {
        dispatch(invalidateFetch(fetchKeyFor(failedActionRef.current)))
      }
    },
    [dispatch]
  )

  const errorFor = (action: BulkActionType) => {
    if (failedAction !== action) return undefined
    return (action === 'delete' ? deleteError : updateError)?.message
  }

  const withClearedFailure =
    (setOpen: (open: boolean) => void) => (open: boolean) => {
      if (!open) requestRef.current += 1
      clearFailure()
      setOpen(open)
    }
  const setUnconfirmDialogOpen = withClearedFailure(setUnconfirmOpen)
  const setConfirmDialogOpen = withClearedFailure(setConfirmOpen)
  // BulkUpdateTransactionDialog submits on its own, so its refusals never reach markFailed.
  const setBulkUpdateDialogOpen = (open: boolean) => {
    clearFailure()
    dispatch(invalidateFetch(BULK_UPDATE_USER_TRANSACTIONS_KEY))
    setBulkUpdateOpen(open)
  }
  const setExcludeDialogOpen = withClearedFailure(setExcludeOpen)
  const setDeleteDialogOpen = withClearedFailure(setDeleteOpen)

  const updateTransactions = async (updateType: BulkActionType) => {
    clearFailure()
    const request = requestRef.current
    if (updateType === 'delete') {
      const deleted = await adminBulkDeleteTransaction(selectedTransactionKeys)(
        dispatch
      )
      if (!deleted) {
        markFailed('delete', request)
        return
      }
      onAction({ type: 'delete', transactionIds: selectedTransactionKeys })
    } else {
      const data: BulkUpdatePayload = {
        transactionIds: selectedTransactionKeys,
      }

      if (updateType === 'unconfirm') {
        data.confirmedAt = null
      } else if (updateType === 'confirm') {
        data.confirmedAt = Date.now()
        data.requestedClarificationAt = null
        data.notifiedRequestedClarificationAt = null
      } else if (updateType === 'exclude') {
        data.excludedAt = Date.now()
        data.type = TransactionRuleCategoryType.personal
      } else if (updateType === 'needsClarification') {
        data.requestedClarificationAt = Date.now()
      }
      const updatedTransactions =
        await bulkUpdateUserTransactions(data)(dispatch)
      if (!updatedTransactions) {
        markFailed(updateType, request)
        return
      }
      onAction({ type: updateType, transactions: updatedTransactions })
    }

    setExcludeOpen(false)
    setConfirmOpen(false)
    setDeleteOpen(false)
    setUnconfirmOpen(false)
    dispatch(clearSelectedTransactions())
  }

  const count = selectedTransactionKeys.length
  const needsClarificationError = errorFor('needsClarification')

  if (count === 0) {
    if (userId) {
      return (
        <RuleCreationModal
          modalOpen={ruleModalOpen}
          setModalOpen={setRuleModalOpen}
          userId={userId}
        />
      )
    }

    return null
  }

  return (
    <div className="fixed bottom-14 right-10 z-20 flex items-center gap-2 bg-neutral-50 p-4">
      <Text variant={TypographyStyles.EYEBROW}>{count} items selected</Text>
      <Dialog
        open={unconfirmOpen}
        onClose={() => setUnconfirmDialogOpen(false)}
      >
        <Dialog.Trigger>
          <Button
            onClick={() => setUnconfirmDialogOpen(true)}
            disabled={someUnconfirmed || isSaving}
          >
            Bulk Unconfirm
          </Button>
        </Dialog.Trigger>
        <Dialog.Content>
          <Dialog.Header>Unconfirm Transactions</Dialog.Header>
          <Dialog.Description>
            Are you sure you want to unconfirm these transactions?
          </Dialog.Description>
          {errorFor('unconfirm') && (
            <Alert variant="error">{errorFor('unconfirm')}</Alert>
          )}
          <Dialog.Footer>
            <Button onClick={() => setUnconfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => updateTransactions('unconfirm')}
              disabled={isSaving}
              loading={isSaving}
            >
              Unconfirm
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
      <BulkConfirmTransactionDialog
        selectedTransactions={selectedTransactions}
        open={confirmOpen}
        onConfirm={() => updateTransactions('confirm')}
        setOpen={setConfirmDialogOpen}
        disabled={someConfirmed || isSaving}
        isSaving={isSaving}
        error={errorFor('confirm')}
      />

      <BulkUpdateTransactionDialog
        open={bulkUpdateOpen}
        setOpen={setBulkUpdateDialogOpen}
        onApply={(updatedTransactions) => {
          if (updatedTransactions) {
            onAction({ type: 'update', transactions: updatedTransactions })
          }
          setRuleModalOpen(true)
        }}
        selectedTransactionKeys={selectedTransactionKeys}
        isSaving={isSaving}
        disabled={someConfirmed || isSaving}
      />

      <Dialog open={excludeOpen} onClose={() => setExcludeDialogOpen(false)}>
        <Dialog.Trigger>
          <Button
            onClick={() => setExcludeDialogOpen(true)}
            disabled={someConfirmed || isSaving}
          >
            Bulk Exclude
          </Button>
        </Dialog.Trigger>
        <Dialog.Content>
          <Dialog.Header>Exclude Transactions</Dialog.Header>
          <Dialog.Description>
            Are you sure you want to exclude these transactions? This will set
            the expense type to personal and remove it from reports.
          </Dialog.Description>
          {errorFor('exclude') && (
            <Alert variant="error">{errorFor('exclude')}</Alert>
          )}
          <Dialog.Footer>
            <Button onClick={() => setExcludeDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => updateTransactions('exclude')}
              disabled={isSaving}
              loading={isSaving}
            >
              Exclude
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      <Button
        onClick={() => updateTransactions('needsClarification')}
        disabled={isSaving || someConfirmed}
        loading={isSaving}
      >
        Mark Needs Clarification
      </Button>
      {needsClarificationError && (
        <Alert variant="error" dismissible onClose={clearFailure}>
          {needsClarificationError}
        </Alert>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteDialogOpen(false)}>
        <Dialog.Trigger>
          <Button onClick={() => setDeleteDialogOpen(true)} disabled={isSaving}>
            Bulk Delete
          </Button>
        </Dialog.Trigger>
        <Dialog.Content>
          <Dialog.Header>Delete Transactions</Dialog.Header>
          <Dialog.Description>
            Are you sure you want to delete these transactions?
          </Dialog.Description>
          {errorFor('delete') && (
            <Alert variant="error">{errorFor('delete')}</Alert>
          )}
          <Dialog.Footer>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => updateTransactions('delete')}
              disabled={isSaving}
              loading={isSaving}
            >
              Delete
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  )
}

export default BulkTransactionActions
